import React, {useState, useEffect} from "react";
import ReplayIcon from "@material-ui/icons/Replay";
import {Typography, Paper, IconButton, Tooltip} from "@material-ui/core";
import HelpIcon from "@material-ui/icons/Help";
import InfoIcon from "@material-ui/icons/Info";
import {isEmpty} from "underscore";

import {
  removeFieldOverride,
  setOverrideFieldValue,
} from "../utils/issue_overrides";
import {issueOverrideFields} from "../constants/issue_override_fields";

import {
  IssueId,
  OverrideFieldName,
  PerIssuesetOverrideTagValues,
  PlaybookIssue,
} from "common/types/issue";
import {PlaybookIssueset} from "common/types/issueset";
import {IssueOverrideTag} from "common/types/issue_override_tag";
import ExistingOverrideTags from "./existing_override_tags";
import Editor from "common_components/editor";

interface IssueOverridesEditorProps {
  issue: PlaybookIssue;
  selectedIssueset?: PlaybookIssueset;
  selectedIssueOverrideTag: string;
  updateIssueOverrideTagValues: (
    issueId: IssueId,
    overrides: PerIssuesetOverrideTagValues,
  ) => void;
  issueOverrideTags: IssueOverrideTag[];
}

const styles = {
  fieldsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  fieldContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  fieldRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  iconSpace: {
    width: 40,
    flexShrink: 0,
    marginTop: "16px",
  },
  titleMain: {
    fontSize: "1rem",
    marginRight: "1em",
    textTransform: "uppercase",
  },
  title: {
    fontSize: "1rem",
  },
  headerContainer: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
  },
  overridesSection: {
    backgroundColor: "#fff",
    borderRadius: "4px",
    margin: "24px",
    border: "1px solid #eee",
  },
  overridesHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    borderBottom: "1px solid #eee",
    background: "#dbdbdb",
  },
  overridesTitle: {
    fontSize: "1.1rem",
    fontWeight: 500,
    color: "#232e38",
    letterSpacing: "0.15px",
  },
  overridesContent: {
    padding: "24px",
  },
  infoMessage: {
    backgroundColor: "#f5f5f5",
    color: "#666",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "24px",
    padding: "16px",
  },
  infoContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
} as const;

const IssueOverridesEditor: React.FC<IssueOverridesEditorProps> = ({
  issue,
  selectedIssueset,
  selectedIssueOverrideTag,
  updateIssueOverrideTagValues,
  issueOverrideTags,
}) => {
  const canEditOverrides = Boolean(
    selectedIssueset && selectedIssueOverrideTag,
  );

  const [localEditorValues, setLocalEditorValues] = useState<
    Partial<Record<OverrideFieldName, string>>
  >({});

  useEffect(() => {
    setLocalEditorValues({});
  }, [issue.id, selectedIssueset?.id, selectedIssueOverrideTag]);

  const getOverridePath = (field: OverrideFieldName) =>
    selectedIssueset && selectedIssueOverrideTag
      ? issue.override_tag_values?.[selectedIssueset.id]?.[
          selectedIssueOverrideTag
        ]?.fields?.[field]
      : null;

  const getFieldValue = (field: OverrideFieldName): string => {
    return getOverridePath(field) ?? issue[field] ?? "";
  };

  const hasOverride = (field: OverrideFieldName): boolean =>
    Boolean(getOverridePath(field));

  const updateOverrides = (field: OverrideFieldName, value: string) => {
    if (!selectedIssueset) {
      return;
    }

    updateIssueOverrideTagValues(
      issue.id,
      setOverrideFieldValue(
        issue.override_tag_values || {},
        selectedIssueset.id,
        parseInt(selectedIssueOverrideTag, 10),
        field,
        value,
      ),
    );
  };

  const handleChange = (field: OverrideFieldName) => (newValue: string) => {
    if (canEditOverrides) {
      setLocalEditorValues(prev => ({
        ...prev,
        [field]: newValue,
      }));
    }
  };

  const handleBlur = (field: OverrideFieldName) => () => {
    if (!canEditOverrides) {
      return;
    }

    const currentValue = getFieldValue(field).trim();
    const newValue = localEditorValues[field]?.trim() ?? currentValue;

    if (
      newValue === currentValue ||
      (isEmpty(newValue) && !hasOverride(field))
    ) {
      return;
    }

    updateOverrides(field, newValue);
  };

  const handleReset = (field: OverrideFieldName) => () => {
    if (!selectedIssueset) {
      return;
    }

    setLocalEditorValues({});
    updateIssueOverrideTagValues(
      issue.id,
      removeFieldOverride(
        issue.override_tag_values || {},
        selectedIssueset.id,
        parseInt(selectedIssueOverrideTag, 10),
        field,
      ),
    );
  };

  return (
    <Paper>
      <div style={{padding: ".5rem"}}>
        <div style={styles.overridesSection}>
          <div style={styles.overridesHeader}>
            <Typography style={styles.overridesTitle}>
              Issue Overrides
            </Typography>
            <Tooltip title="Customise fields for this issue based on project tags. Changes will only affect projects using the selected tag">
              <IconButton size="small">
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div style={styles.overridesContent}>
            {!selectedIssueOverrideTag && (
              <div style={styles.infoContainer}>
                <div style={{flex: 1}}>
                  <div style={styles.infoMessage}>
                    <InfoIcon style={{color: "#666"}} />
                    <span>
                      Select an override tag from the dropdown menu to customise
                      this issue for projects using the selected tag
                    </span>
                  </div>
                </div>
                <div style={styles.iconSpace} />
              </div>
            )}

            <div style={styles.fieldsContainer}>
              {issueOverrideFields.map(({field, label}) => (
                <div key={field} style={styles.fieldContainer}>
                  <div style={styles.fieldRow}>
                    <div style={{width: "calc(100% - 40px)"}}>
                      <Editor
                        value={localEditorValues[field] ?? getFieldValue(field)}
                        onChange={handleChange(field)}
                        onBlur={handleBlur(field)}
                        label={label}
                        disabled={!canEditOverrides}
                      />
                    </div>
                    <div style={styles.iconSpace}>
                      {hasOverride(field) && (
                        <IconButton
                          onClick={handleReset(field)}
                          disabled={!canEditOverrides}
                          size="small"
                          title="Reset to default"
                        >
                          <ReplayIcon />
                        </IconButton>
                      )}
                    </div>
                  </div>
                  {selectedIssueset && (
                    <ExistingOverrideTags
                      issue={issue}
                      issuesetId={selectedIssueset.id}
                      field={field}
                      selectedTag={selectedIssueOverrideTag}
                      issueOverrideTags={issueOverrideTags}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Paper>
  );
};

export default IssueOverridesEditor;
